import { studyAccessMessage } from './studyAccessMessages';
import type { StudyAnalysisRefund, StudyAnalysisRefundState } from './studyAnalysisApi';

/**
 * Learner copy for Study page analysis (study-capture-analyse-prd.md §3.6, §6, §8). Messages say
 * what happened and what still works. They never show an internal code, a receipt or a request
 * id. Errors are read by shape, so a mocked API client in tests behaves like the real one.
 */

export const ANALYSIS_NOT_FOR_SCHOOL = 'Not available for school accounts yet';
export const ANALYSIS_LOW_BALANCE =
  'Not enough Stars right now. Ask a parent to top up in the Family Portal.';
export const ANALYSIS_IMAGE_REJECTED = "We can't analyse this image.";
export const ANALYSIS_FAILED = "Analysis didn't work this time.";
export const ANALYSIS_STARS_BACK = 'Your Stars are back.';
export const ANALYSIS_STARS_RETURNING = "We're returning your Stars.";
export const ANALYSIS_FAILED_REFUNDED = `${ANALYSIS_FAILED} ${ANALYSIS_STARS_BACK}`;
export const ANALYSIS_FAILED_REFUND_PENDING = `${ANALYSIS_FAILED} ${ANALYSIS_STARS_RETURNING}`;
/** Shown in place of "Try again · N★" while Stars of this analysis are still owed. */
export const ANALYSIS_RETRY_AFTER_REFUND = 'You can try again after your Stars are back.';
/** The runner could not save the refund; the Stars are still owed (capture PRD §15.3). */
export const ANALYSIS_REFUND_PENDING = 'REFUND_PENDING';
export const ANALYSIS_ASSESSMENT_NOTICE =
  'This looks like an assessment. Tutor will give hints, not answers.';
export const ANALYSIS_STARS_FOR_UNREAD = 'Stars for pages that could not be read are back.';
/** G-01: an assessment is never checked (academic integrity, capture PRD §6.8). */
export const CHECK_OFF_FOR_ASSESSMENT = 'Checking answers is off for assessments.';
/** G-02: no practice set on an assessment; it would show answers and worked steps (§6.8). */
export const PRACTICE_OFF_FOR_ASSESSMENT = 'Practice is off for assessments.';

interface ErrorShape {
  status?: number;
  code?: string;
  message?: string;
  details?: unknown;
}

function shapeOf(error: unknown): ErrorShape | null {
  if (!error || typeof error !== 'object') return null;
  const value = error as ErrorShape;
  return typeof value.code === 'string' ? value : null;
}

function detailsOf(error: ErrorShape): Record<string, unknown> {
  return error.details && typeof error.details === 'object'
    ? (error.details as Record<string, unknown>)
    : {};
}

export function errorCode(error: unknown): string | null {
  return shapeOf(error)?.code ?? null;
}

/** The current price after a 409 AI_PRICE_CHANGED, or null. */
export function changedPrice(error: unknown): number | null {
  const shape = shapeOf(error);
  if (shape?.code !== 'AI_PRICE_CHANGED') return null;
  const details = detailsOf(shape);
  const top = error as Record<string, unknown>;
  const value = details.current_stars ?? top.current_stars;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** True when this learner cannot use page analysis at all (school account, no family, Hangar). */
export function analysisUnavailable(error: unknown): boolean {
  return shapeOf(error)?.code === 'STUDY_ANALYSIS_NOT_AVAILABLE';
}

function walletMessage(error: ErrorShape): string {
  switch (detailsOf(error).kind) {
    case 'daily':
      return "You've used today's Stars limit. Ask a parent, or try again tomorrow.";
    case 'weekly':
      return "You've used this week's Stars limit. Ask a parent.";
    case 'monthly':
      return "You've used this month's Stars limit. Ask a parent.";
    case 'per_request':
      return 'This costs more Stars than your family allows at once. Ask a parent.';
    default:
      return ANALYSIS_LOW_BALANCE;
  }
}

/** Copy for a failed quote, create, retry or edit call. */
export function analysisErrorMessage(error: unknown, fallback: string): string {
  const shape = shapeOf(error);
  if (!shape) return fallback;
  const access = studyAccessMessage(error);
  if (access) return access;
  switch (shape.code) {
    case 'STUDY_AI_NOT_CONFIGURED':
      return 'Learning AI is not connected yet. Your pages are saved and no Stars were charged. You can reopen this material and try again later.';
    case 'STUDY_AI_VISION_NOT_CONFIGURED':
      return 'Image and formula reading is not connected yet. Your pages are saved and no Stars were charged. Try again when image reading is ready.';
    case 'STUDY_PDF_PROCESSOR_UNAVAILABLE':
      return 'PDF reading is temporarily unavailable. Your uploaded file is saved. Try again shortly.';
    case 'STUDY_NOTE_ORIGINAL_SCOPE_TOO_LARGE':
      return 'Choose a few learning points to use their original source passages. No Stars were charged.';
    case 'STUDY_NOTE_WORKING_UNAVAILABLE':
      return 'Your saved working changed or is no longer available. Save it, then select the saved version again.';
    case 'STUDY_NOTE_WORKING_INVALID':
    case 'STUDY_NOTE_WORKING_IMAGE_INVALID':
      return 'This saved working cannot be read safely. Your original drawing is kept; leave it out or try another saved page.';
    case 'STUDY_AUDIO_SCRIPT_CHANGED':
      return 'This audio script changed or was already confirmed. Reopen the saved item before continuing.';
    case 'STUDY_AUDIO_SCRIPT_REVIEW_REQUIRED':
      return 'Remove personal or unsafe details from the script, then review it again.';
    case 'STUDY_ANALYSIS_NOT_AVAILABLE':
      return shape.message === 'Page analysis is not available on this computer yet.'
        ? shape.message
        : ANALYSIS_NOT_FOR_SCHOOL;
    case 'STUDY_ANALYSIS_PAUSED':
      return 'Page analysis is paused right now. Your material is saved.';
    case 'STUDY_ANALYSIS_ROUTE_UNAVAILABLE':
    case 'STUDY_ANALYSIS_PRICE_INVALID':
      return 'Page analysis is not available right now. Your material is saved.';
    case 'AI_PRICE_CHANGED': {
      const price = changedPrice(error);
      return price === null
        ? 'The price changed. Check the new price and try again.'
        : `The price is now ${price}★. Check it and try again.`;
    }
    case 'WALLET_INSUFFICIENT':
      return walletMessage(shape);
    case 'FAMILY_PAUSED':
      return 'Your family paused AI. Ask a parent.';
    case 'MODERATION_REJECTED':
      return ANALYSIS_IMAGE_REJECTED;
    case 'MODERATION_UNAVAILABLE':
    case 'SAFETY_UNAVAILABLE':
      return 'The analysis safety check is unavailable. Your material is saved. Try again later.';
    case 'STUDY_PAGE_IMAGE_INVALID':
      return "This page image can't be used. Take the photo again.";
    case 'STUDY_PAGE_UPLOAD_INCOMPLETE':
    case 'STUDY_PAGE_KEY_INVALID':
    case 'STUDY_ANALYSIS_PAGE_NOT_UPLOADED':
      return 'A page did not finish uploading. Try again.';
    case 'STUDY_DOCUMENT_NOT_FOUND':
    case 'STUDY_ANALYSIS_NOT_FOUND':
      return 'This material is no longer here. It may have been deleted.';
    case 'STUDY_ANALYSIS_IDEMPOTENCY_CONFLICT':
      return 'The pages changed. Check them and try again.';
    case 'STUDY_ANALYSIS_NOT_READY':
      return 'Wait until the analysis finishes, then try again.';
    case 'STUDY_ANALYSIS_NOT_RETRYABLE':
      return 'This analysis is already finished.';
    case 'STUDY_ANALYSIS_EDIT_INVALID':
      return "This change can't be saved. Refresh the page and try again.";
    case 'STUDY_DOCUMENT_NOT_ORGANISED':
      return 'Choose a subject for this material first. Use Change above.';
    // Check my answers, practice sets and Review (study-capture-fixes-prd.md G-01 to G-05).
    case 'STUDY_ANSWER_CHECK_ASSESSMENT':
      return CHECK_OFF_FOR_ASSESSMENT;
    case 'STUDY_ANSWER_CHECK_NO_ANSWERS':
      return "There's no answer on the page to check. Write your answer, then take a new photo.";
    case 'STUDY_ANALYSIS_QUESTION_NOT_FOUND':
      return 'This question is not in the list any more. Refresh and try again.';
    case 'STUDY_ANSWER_CHECK_IDEMPOTENCY_CONFLICT':
    case 'STUDY_PRACTICE_SET_IDEMPOTENCY_CONFLICT':
      return 'Something changed. Check it and try again.';
    case 'STUDY_PAID_PRICE_INVALID':
      return 'This is not available right now. Try again later.';
    case 'STUDY_PRACTICE_SET_NOT_FOUND':
    case 'STUDY_PRACTICE_QUESTION_NOT_FOUND':
      return 'This practice question is no longer here. Refresh and try again.';
    case 'STUDY_PRACTICE_SET_NOT_READY':
      return "These practice questions aren't ready.";
    case 'STUDY_PRACTICE_ATTEMPT_INVALID':
      return 'Write your answer or pick an option first.';
    case 'STUDY_ANALYSIS_NOT_MARKED_TEST':
      return 'This only works for a marked test.';
    case 'STUDY_ANALYSIS_REVIEW_NOT_SAVED':
      return "Review wasn't saved. Try again.";
    default:
      return fallback;
  }
}

interface FailedAnalysisShape {
  error_code: string | null;
  stars: { charged: number; refunded: number };
  /** Absent only on a response from an older server. */
  refund?: StudyAnalysisRefund | null;
}

/**
 * The refund state of an analysis (capture PRD §15.3). The server's `refund.state` decides; a
 * `REFUND_PENDING` row that still owes Stars is always `pending`, so the app never says
 * "Your Stars are back." before they are.
 */
export function analysisRefundState(analysis: FailedAnalysisShape): StudyAnalysisRefundState {
  const owed = analysis.refund?.owed_stars ?? analysis.stars.charged - analysis.stars.refunded;
  if (analysis.refund?.state === 'pending') return 'pending';
  if (analysis.error_code === ANALYSIS_REFUND_PENDING && owed > 0) return 'pending';
  if (analysis.refund) return analysis.refund.state;
  return analysis.stars.refunded > 0 && owed <= 0 ? 'refunded' : 'none';
}

/** True while Stars of this analysis are still owed: a paid retry waits until they are back. */
export function analysisRefundPending(analysis: FailedAnalysisShape): boolean {
  return analysisRefundState(analysis) === 'pending';
}

/** The refund sentence after a failure, or an empty string when nothing was charged. */
export function analysisRefundSentence(analysis: FailedAnalysisShape): string {
  switch (analysisRefundState(analysis)) {
    case 'pending':
      return ANALYSIS_STARS_RETURNING;
    case 'refunded':
      return ANALYSIS_STARS_BACK;
    default:
      return '';
  }
}

/** Copy for a whole-analysis failure. It says the Stars are back only when they are. */
export function analysisFailureMessage(analysis: FailedAnalysisShape): string {
  const state = analysisRefundState(analysis);
  if (state === 'pending') return ANALYSIS_FAILED_REFUND_PENDING;
  if (state === 'none') return ANALYSIS_FAILED;
  if (analysis.error_code === 'LLM_OUTPUT_REJECTED')
    return `${ANALYSIS_IMAGE_REJECTED} ${ANALYSIS_STARS_BACK}`;
  if (analysis.error_code === 'STUDY_ANALYSIS_PAUSED')
    return `Page analysis was paused before it finished. ${ANALYSIS_STARS_BACK}`;
  return ANALYSIS_FAILED_REFUNDED;
}
